import React, { useState, useCallback, useRef } from 'react';
import {
  Radio,
  RadioGroup,
  FormControlLabel,
  TextField,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core';
import { t as translate } from 'ttag';
import { BaseDialog } from '@coolremote/shared';

const useStyles = makeStyles(() => ({
  inputContainer: {
    marginTop: '16px',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
}));

const SaveConfirmationDialog = ({ open, onClose, templateName }) => {
  const classes = useStyles();

  const [selectedOption, setSelectedOption] = useState('existing');
  const [newName, setNewName] = useState('');

  const handleOptionChange = (event) => {
    setSelectedOption(event.target.value);
  };

  const handleNameChange = (event) => {
    setNewName(event.target.value);
  };

  const handleConfirm = () => {
    const result = {
      isNew: selectedOption === 'new',
      newName: selectedOption === 'new' ? newName : null,
    };
    onClose(result);
  };

  const handleCancel = () => {
    onClose(null);
  };

  const isConfirmDisabled = selectedOption === 'new' && !newName;

  const mainText = templateName
    ? translate`You made changes to the template '${templateName}', would you like to update the existing template or create a new one?`
    : translate`Do you want to save the changes as a new template or update the existing one?`;

  return (
    <BaseDialog
      open={open}
      onClose={handleCancel}
      onCancel={handleCancel}
      onConfirm={handleConfirm}
      title={translate`Choose an Option`}
      cancelLabel={translate`Cancel`}
      confirmLabel={translate`Confirm`}
      confirmDisabled={isConfirmDisabled}
    >
      <p>{mainText}</p>
      <div className={classes.inputContainer}>
        <RadioGroup value={selectedOption} onChange={handleOptionChange}>
          <FormControlLabel
            value="existing"
            control={<Radio />}
            label={translate`Save Existing`}
          />
          <FormControlLabel
            value="new"
            control={<Radio />}
            label={translate`Create New`}
          />
        </RadioGroup>
        {selectedOption === 'new' && (
          <TextField
            label={translate`New Name`}
            value={newName}
            onChange={handleNameChange}
            fullWidth
            margin="dense"
          />
        )}
      </div>
    </BaseDialog>
  );
};

const useSaveConfirmationDialog = () => {
  const [isOpen, setIsOpen] = useState(false);
  const resolveRef = useRef();

  const showDialog = useCallback(() => {
    return new Promise((resolve) => {
      resolveRef.current = resolve;
      setIsOpen(true);
    });
  }, []);

  const handleClose = useCallback((result) => {
    setIsOpen(false);
    resolveRef.current?.(result);
  }, []);

  const DialogComponent = useCallback(
    (props) => <SaveConfirmationDialog open={isOpen} onClose={handleClose} {...props} />,
    [isOpen, handleClose]
  );

  return [showDialog, DialogComponent];
};

export default useSaveConfirmationDialog;
